/**
 * "Agendamentos" → "agendamento": o nome de UM item de uma tabela, para o botão
 * que acrescenta um. A definição só guarda o nome da tabela (no plural), então a
 * primeira palavra vai ao singular pelas regras do português; noutras línguas o
 * nome fica como está — melhor no plural que inventado.
 *
 * shortcut: regras do plural regular do português, sem dicionário; se as tabelas
 * ganharem um "nome de um item" próprio, ele substitui isto.
 */
const RULES: ReadonlyArray<readonly [RegExp, string]> = [
	[/ões$/, "ão"],
	[/ães$/, "ão"],
	[/ais$/, "al"],
	[/éis$/, "el"],
	[/eis$/, "el"],
	[/óis$/, "ol"],
	[/ns$/, "m"],
	[/([rz])es$/, "$1"],
	[/([aeo])s$/, "$1"],
];

/** Onde o nome deixa de concordar com o item: "Itens | dos pedidos". */
const CONNECTOR = /^(d[oa]s?|n[oa]s?|a[os]?|às?|o|para|pel[oa]s?|com|em|por|e|de)$/i;

/** Uma palavra no singular, se o plural regular a reconhece; `null` se não é plural. */
function singular(word: string): string | null {
	const rule = word.length > 3 ? RULES.find(([plural]) => plural.test(word)) : undefined;
	return rule ? word.replace(rule[0], rule[1]) : null;
}

export function itemNoun(tableName: string, locale: string): string {
	if (!locale.startsWith("pt")) return tableName;
	const [first = "", ...rest] = tableName.split(" ");
	const word = singular(first) ?? first;
	// Os adjetivos que seguem o nome concordam com ele ("Pagamentos recebidos" →
	// "pagamento recebido"), até a primeira preposição ou palavra que não é plural.
	const agree = rest.findIndex((w) => CONNECTOR.test(w) || singular(w) === null);
	const tail = agree === -1 ? rest.length : agree;
	const adjectives = rest.slice(0, tail).map((w) => singular(w) ?? w);
	// Um nome todo em maiúsculas é sigla ("SST"): fica como veio.
	const lead = word === word.toUpperCase() ? word : word.charAt(0).toLowerCase() + word.slice(1);
	return [lead, ...adjectives, ...rest.slice(tail)].join(" ");
}
